import type { SpreadsheetCommandId } from './commandRegistry'

export type RibbonViewCommandId = Extract<
  SpreadsheetCommandId,
  'freeze-panes' | 'freeze-top-row' | 'freeze-first-column' | 'unfreeze-panes' | 'gridlines' | 'zoom'
>

export type RibbonViewCommandContext = {
  hasViewActions: boolean
  gridlinesVisible?: boolean
  frozenPaneRows: number
  frozenPaneCols: number
  zoomPercent: number
}

export type RibbonViewCommandPlan =
  | { action: 'freeze-selection'; notifyViewStateChanged: true }
  | { action: 'set-frozen-panes'; rows: number; cols: number; notifyViewStateChanged: true }
  | { action: 'toggle-gridlines'; notifyViewStateChanged: true }
  | { action: 'set-zoom'; zoom: number; notifyViewStateChanged: true }

function normalizedCount(value: unknown): number {
  const next = Math.floor(Number(value))
  return Number.isFinite(next) ? Math.max(0, next) : 0
}

function normalizedZoomPercent(zoom: unknown): number {
  const value = Number(zoom)
  return Number.isFinite(value) && value > 0 ? Math.round(value * 100) : 100
}

export function buildRibbonViewCommandContext(args: {
  mockMode?: boolean
  hasController: boolean
  gridlinesVisible?: boolean
  frozenPaneRows?: number
  frozenPaneCols?: number
  zoom?: number
}): RibbonViewCommandContext {
  if (args.mockMode) {
    return {
      hasViewActions: true,
      gridlinesVisible: true,
      frozenPaneRows: 1,
      frozenPaneCols: 1,
      zoomPercent: 100,
    }
  }
  return {
    hasViewActions: Boolean(args.hasController),
    gridlinesVisible: args.gridlinesVisible,
    frozenPaneRows: normalizedCount(args.frozenPaneRows),
    frozenPaneCols: normalizedCount(args.frozenPaneCols),
    zoomPercent: normalizedZoomPercent(args.zoom),
  }
}

export function buildRibbonViewCommandPlan(commandId: RibbonViewCommandId): RibbonViewCommandPlan {
  switch (commandId) {
    case 'freeze-panes':
      return { action: 'freeze-selection', notifyViewStateChanged: true }
    case 'freeze-top-row':
      return { action: 'set-frozen-panes', rows: 1, cols: 0, notifyViewStateChanged: true }
    case 'freeze-first-column':
      return { action: 'set-frozen-panes', rows: 0, cols: 1, notifyViewStateChanged: true }
    case 'unfreeze-panes':
      return { action: 'set-frozen-panes', rows: 0, cols: 0, notifyViewStateChanged: true }
    case 'gridlines':
      return { action: 'toggle-gridlines', notifyViewStateChanged: true }
    case 'zoom':
      return { action: 'set-zoom', zoom: 1, notifyViewStateChanged: true }
  }
}
